import { useEffect, useState, type ChangeEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { mockInstitutions } from '../mockInstitutions';
import { DetailPageLayout } from '@/components/Layout/DetailPageLayout';
import { Button, Card, InfoItem, Input } from '@/components/ui';
import { formatDateTime } from '@/utils/dateTime';

export default function InstitutionEdit() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const institutionId = id ? Number(id) : NaN;
  const institution = mockInstitutions.find((item) => item.id === institutionId);
  const [state, setState] = useState<'NORMAL' | 'DELETED'>(institution?.state ?? 'NORMAL');
  const [selectedImageFile, setSelectedImageFile] = useState<File | null>(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(institution?.imageUrl ?? null);

  const handleImageFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const previewUrl = URL.createObjectURL(file);
    setSelectedImageFile(file);
    setImagePreviewUrl((prev) => {
      if (prev?.startsWith('blob:')) {
        URL.revokeObjectURL(prev);
      }
      return previewUrl;
    });
  };

  useEffect(() => {
    return () => {
      if (imagePreviewUrl?.startsWith('blob:')) {
        URL.revokeObjectURL(imagePreviewUrl);
      }
    };
  }, [imagePreviewUrl]);

  return (
    <DetailPageLayout
      title="기관 수정"
      subtitle={institution ? `ID: ${institution.id} | ${institution.code}` : undefined}
      onBack={() => navigate(`/dashboard/institutions/${institutionId}`)}
      isError={!institution}
      errorMessage="수정할 기관 정보를 찾을 수 없습니다."
      actions={
        <Button variant="primary" disabled>
          저장 준비중
        </Button>
      }
      secondaryActions={
        <Button variant="outline" onClick={() => navigate(`/dashboard/institutions/${institutionId}`)}>
          취소
        </Button>
      }>
      {institution && (
        <>
          <Card title="기본 정보">
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <InfoItem label="기관 ID" value={institution.id.toString()} />
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700">상태</label>
                <select
                  value={state}
                  onChange={(e) => setState(e.target.value as 'NORMAL' | 'DELETED')}
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm shadow-sm focus:border-blue-500 focus:ring-blue-500">
                  {/* todo: 상수 데이터로 수정 될 예정 */}
                  <option value="NORMAL">NORMAL</option>
                  <option value="DELETED">DELETED</option>
                </select>
              </div>
              <Input label="기관명" value={institution.name} />
              <InfoItem label="기관 코드" value={institution.code} />
              <InfoItem label="생성일" value={formatDateTime(institution.createdAt)} />
              <InfoItem label="수정일" value={formatDateTime(institution.updatedAt)} />
            </div>
          </Card>

          <Card title="기관 이미지">
            <div className="space-y-4">
              {imagePreviewUrl ? (
                <div className="flex items-center gap-6">
                  <img
                    src={imagePreviewUrl}
                    alt={`${institution.name} 로고`}
                    className="h-24 w-24 rounded-lg border border-slate-200 object-cover"
                  />
                  <div className="text-sm text-slate-600">
                    {selectedImageFile ? (
                      <>
                        <p className="font-medium text-slate-900">{selectedImageFile.name}</p>
                        <p>{Math.round(selectedImageFile.size / 1024)} KB</p>
                      </>
                    ) : (
                      <p>현재 등록된 이미지</p>
                    )}
                  </div>
                </div>
              ) : (
                <p className="text-sm text-slate-500">등록된 이미지가 없습니다.</p>
              )}

              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700">이미지 업로드</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageFileChange}
                  className="block w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm file:mr-3 file:rounded-md file:border-0 file:bg-slate-100 file:px-3 file:py-1.5 file:text-sm file:font-medium hover:file:bg-slate-200"
                />
              </div>
            </div>
          </Card>
        </>
      )}
    </DetailPageLayout>
  );
}
